import { z } from "zod";

/**
 * Every Admin API response uses this envelope (spec §12 "API shape").
 * The worker and the admin app both import these, so the two cannot drift.
 */

export interface ApiSuccess<T> {
  success: true;
  data: T;
}

export interface ApiFailure {
  success: false;
  error: { code: string; message: string };
}

export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export function ok<T>(data: T): ApiSuccess<T> {
  return { success: true, data };
}

export function err(code: string, message: string): ApiFailure {
  return { success: false, error: { code, message } };
}

export const apiFailureSchema = z.object({
  success: z.literal(false),
  error: z.object({ code: z.string(), message: z.string() }),
});

export function apiSuccessSchema<T extends z.ZodTypeAny>(data: T) {
  return z.object({ success: z.literal(true), data });
}

/** Parse an unknown response body into a typed envelope. */
export function apiResponseSchema<T extends z.ZodTypeAny>(data: T) {
  return z.union([apiSuccessSchema(data), apiFailureSchema]);
}

export const healthSchema = z.object({
  status: z.literal("ok"),
  environment: z.string(),
  time: z.iso.datetime(),
});

export type Health = z.infer<typeof healthSchema>;
